import React, { memo, useCallback, useMemo } from "react";
import { Wrap, WrapItem } from "@chakra-ui/react";
import FileThumb from "modules/files/file-thumb";
import {
  FileFragment,
  MessageFragment,
  MessageThreadFragment,
} from "gql/graphql";
import { isAudio, sortById } from "modules/utils/Functions";
import MessageAudioFile from "modules/messaging/messages/message-audio-file";
import colors from "app/colors";
import { MESSAGE_STATE } from "modules/utils/Constants";
import { useOverlay } from "modules/providers/overlay-provider";
import { MODAL_TYPE } from "modules/providers/overlay-provider/overlay-provider";
import MessageFile from "./message-file";
import useMessageThreadTranslation from "modules/hooks/use-message-thread-translation";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
  messageState: MESSAGE_STATE;
  fileThumbSize?: [number, number];
}

const MessageFiles = ({
  messageThread,
  message,
  messageState,
  fileThumbSize = [235, 180],
}: IProps) => {
  const { openModal } = useOverlay();

  const { isViewingTranslatedMessage } = useMessageThreadTranslation(
    message,
    messageThread,
  );

  const fileList = useMemo(
    () => [...message.fileList].sort(sortById),
    [message.fileList],
  );

  const openGallery = useCallback(
    (fileId: string) => () => {
      openModal({
        type: MODAL_TYPE.MESSAGE_THREAD_FILE_GALLERY,
        id: messageThread.id,
        id2: fileId,
      });
    },
    [messageThread, openModal],
  );

  const isOpenGalleryEnabled = useCallback(
    (file: FileFragment) =>
      messageState !== "not-sent" && !isAudio(file.contentType),
    [messageState],
  );

  return (
    <Wrap w="full" pl={isViewingTranslatedMessage ? 3 : 0}>
      {fileList.map((file) => {
        if (isAudio(file.contentType)) {
          return (
            <WrapItem key={file.id} w="full">
              <MessageFile
                messageThread={messageThread}
                message={message}
                file={file}
                color={colors.gray50}
                messageState={messageState}
                placement="top-end"
                offset={[-6, -42]}
                hover="border"
                displayReactionsGradient={false}
              >
                <MessageAudioFile
                  message={message}
                  file={file}
                  messageThreadLocale={messageThread.locale}
                />
              </MessageFile>
            </WrapItem>
          );
        }
        return (
          <WrapItem
            key={file.id}
            cursor={isOpenGalleryEnabled(file) ? "pointer" : undefined}
            onClick={
              isOpenGalleryEnabled(file) ? openGallery(file.id) : undefined
            }
          >
            <MessageFile
              messageThread={messageThread}
              message={message}
              file={file}
              messageState={messageState}
            >
              <FileThumb
                contentType={file.contentType}
                name={file.name}
                url={file.previewCloudFrontUrl}
                size={fileThumbSize}
              />
            </MessageFile>
          </WrapItem>
        );
      })}
    </Wrap>
  );
};

export default memo(MessageFiles);
